import React, { ChangeEvent, useCallback, useState, useMemo, useRef, useEffect } from "react";
import { Alert } from "antd";
import styles from "./EditContent.module.scss";
import {
  TextInput,
  TextInputValue,
} from "../../../../../components/text-input/TextInput";
import Button from "../../../../../components/button/Button";
import {
  ContentType,
  DefaultModuleEnum,
  SelectOption,
  ExtendedContent,
  UnitTypes,
  FeedbackForm,
} from "../../../../../types";
import { uploadFile, getMediaAbsolutePath } from "../../../../../services/File";
import { LazyQueryExecFunction, MutationFunction } from "@apollo/client";
import ContentAudio from "../../../../../components/content-audio/ContentAudio";
import ContentPDF from "../../../../../components/content-pdf/ContentPDF";
import ContentSlide from "../../../../../components/content-slide/ContentSlide";
import FeedbackFormEditor from "./feedback-form-editor/FeedbackFormEditor";
import VideoForm from "@components/video-form/VideoForm";
import RadioGroup from "@components/radio-group/RadioGroup";
import {
  GetUrlQuery,
  GetUrlQueryVariables,
  DeleteGroupModuleDataMutation,
  DeleteGroupModuleDataMutationVariables,
  DeleteModuleDataMutation,
  DeleteModuleDataMutationVariables,
  Video,
  useUploadZipSlideMutation,
  useGetZipSlideUrlLazyQuery,
} from "@api/graphql-codegen";
import { ReloadOutlined } from "@ant-design/icons";
// import SelectMulti from '../../../../../components/select-multi/SelectMulti';
import Checkbox from "../../../../../components/checkbox/Checkbox";
import { ErrorKeys } from "./useEditContent";
import Spinner from "@components/spinner/Spinner";
import { SURVEY_TYPE } from "@src/constants";
import RevealSlide from "@src/components/reveal-slide/RevealSlide";
import CodeEditor from "@src/components/code-editor/CodeEditor";
import { useAWSKeys } from "@src/hooks";
import Skeleton from "react-loading-skeleton";
import PreviewIcon from "src/assets/icons/preview.svg?react";
import { useToast } from "@src/hooks";
import { isFilePath, isValidUrl } from "@src/utils/helpers";
import { logger } from '../../../../../utils/logger';

interface SlideMessage {
  type: string;
  totalSlides?: number;
  currentPage?: number;
  page?: number;
  subPage?: number;
}

type Props = {
  content?: ExtendedContent;
  formData?: string;
  errors: Partial<Record<ErrorKeys, string>>;
  setErrors: (e: Partial<Record<ErrorKeys, string>>) => void;
  fileLoading?: boolean;
  getUrl: LazyQueryExecFunction<GetUrlQuery, GetUrlQueryVariables>;
  deleteGroupModuleData: MutationFunction<
    DeleteGroupModuleDataMutation,
    DeleteGroupModuleDataMutationVariables
  >;
  deleteModuleData: MutationFunction<
    DeleteModuleDataMutation,
    DeleteModuleDataMutationVariables
  >;
  fileUploadingPercent: number;
  setFileUploadingPercent: (x: number) => void;
  clearFile: () => void;
  handleRemoveGroup: () => void;
  description: string;
  setDescription: (description: string) => void;
  data: string | null;
  setData: (d: string | null) => void;
  dataInputRef: React.RefObject<HTMLInputElement>;
  isDefault: boolean;
  uploadVideoAndSave: (
    file: File,
    data: { url: string; uploadId: string }
  ) => void;
  handleChangeVideo: (video: Video) => void;
  loading?: boolean;
  isGroupRemoving?: boolean;
  isDisabled?: boolean;
  handleContentDataSave: (data: string) => void;
  onChangeRequiredLesson: (v: boolean) => void;
  onChangeLastLessonMark: (v: boolean) => void;
  onChangeAdditionalAfter: (v: boolean) => void;
  isRequired: boolean;
  isLastLesson: boolean;
  isAdditionalAfter: boolean;
  isClassPresentation: boolean;
  fileUploadFinished: boolean;
  onChangeHomeEdUseDefaultState: (value: string) => void;
  homeEdState: DefaultModuleEnum;
  isHomeEdModuleSaved?: boolean;
  currentUnitType: UnitTypes;
  isPreparation?: boolean;
};

const heaOptions: SelectOption[] = [
  { label: "Content same as Default", value: DefaultModuleEnum.Default },
  { label: "Content different from Default", value: DefaultModuleEnum.HomeEd },
];

const exitTicketTypeOptions = [
  {label: "PDF Slide", value: "1"},
  {label: "Slides.com URL", value: "2"},
]

function GenericContent({
  handleRemoveGroup,
  content,
  formData,
  errors,
  setErrors,
  fileLoading,
  getUrl,
  fileUploadingPercent,
  uploadVideoAndSave,
  isDisabled,
  setFileUploadingPercent,
  clearFile,
  description,
  setDescription,
  data,
  setData,
  dataInputRef,
  isDefault,
  loading: overallLoading,
  deleteModuleData,
  deleteGroupModuleData,
  isGroupRemoving,
  handleContentDataSave,
  isRequired,
  onChangeRequiredLesson,
  onChangeLastLessonMark,
  isLastLesson,
  onChangeAdditionalAfter,
  isAdditionalAfter,
  isClassPresentation,
  fileUploadFinished,
  onChangeHomeEdUseDefaultState,
  homeEdState,
  isHomeEdModuleSaved,
  currentUnitType,
  isPreparation,
  handleChangeVideo,
}: Props) {
  const [loading, setLoading] = useState(false);
  const { awsKeys } = useAWSKeys();
  const { successToast, errorToast } = useToast();
  const isMounted = useRef(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [exitTicketSlideType, setExitTicketSlideType] = useState('1');

  useEffect(() => {
    isMounted.current = true; // Component is mounted
    return () => {
      isMounted.current = false; // Component is unmounted
    };
  }, []);

  useEffect(() => {
    // Function to handle messages from iframe
    const handleMessage = (event: MessageEvent<SlideMessage>) => {
      if (event.data.type === "SLIDE_INFO") {
        setCurrentPage(event.data.subPage || 1);
        setTotalPage(event.data.totalSlides || 1);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [setCurrentPage, setTotalPage]);

  const [uploadZIPSlide, { loading: zipUploading }] = useUploadZipSlideMutation({
    onError(error) {
      logger.error(error.message);
      errorToast(error.message);
    },
  });
  const [getZIPSlideUrl, { loading: getSlideURLoading }] = useGetZipSlideUrlLazyQuery({
    fetchPolicy: 'network-only',
    onError(error) {
      logger.error(error.message);
      errorToast(error.message);
    },
  })

  const [url, setUrl] = useState("");

  // Home Ed content lives in its own table family (hea_module), not in a content
  // group. The Home Ed tab identifies its base content with a client-side
  // synthetic groupId ("HomeEd" / "HomeEd_<id>") that exists in no group table --
  // see useEditContent.homeEdGroupModule -- so it must never reach the API, or
  // the write silently matches zero rows. Signal Home Ed with isHomeEd instead.
  const slideTarget = useMemo(() => {
    const rawGroupId = content?.groupId ?? '';
    const isSyntheticHomeEdGroup =
      rawGroupId === DefaultModuleEnum.HomeEd ||
      rawGroupId.startsWith(`${DefaultModuleEnum.HomeEd}_`);
    return {
      id: content?.id || '',
      groupId: isSyntheticHomeEdGroup ? undefined : (rawGroupId || undefined),
      isCourse: true,
      isHomeEd: currentUnitType === UnitTypes.HomeEd,
    };
  }, [content?.groupId, content?.id, currentUnitType]);

  // Slides attach to the hea_module row, which only exists once the module has been saved
  // as "Content different from Default" - switching the radio alone just sets local state.
  // Uploading before that gets rejected by the API, so block it here and say why.
  const needsHomeEdSave = slideTarget.isHomeEd && !isHomeEdModuleSaved;

  const onUpload = useCallback(async() => {
    if (!!url) {
      setLoading(true);

      // Store the current URL to compare against later
      const currentSlideUrl = data || content?.data;

      try {
        const response = await uploadZIPSlide({
          variables: {
            input: {
              groupId: slideTarget.groupId,
              id: slideTarget.id,
              isCourse: slideTarget.isCourse,
              isHomeEd: slideTarget.isHomeEd,
              url: url
            }
          }
        });
        
        if (response.data?.uploadZIPSlide) {
          successToast('Zip file uploaded successfully. Processing slides (this may take up to 2 minutes)...');
          
          const fetchSlideUrl = async (retryCount = 0, maxRetries = 24) => { // Increased to 24 retries (6 minutes total)
            if (!isMounted.current) {
              setLoading(false);
              return;
            }
            
            try {
              const res = await getZIPSlideUrl({
                variables: {
                  groupId: slideTarget.groupId,
                  id: slideTarget.id,
                  isCourse: slideTarget.isCourse,
                  isHomeEd: slideTarget.isHomeEd,
                }
              });
    
              const newSlideUrl = res.data?.getZIPSlideUrl;
              
              // Check if we got a new URL that's different from the original
              if (newSlideUrl && 
                  newSlideUrl.trim() !== '' && 
                  newSlideUrl !== currentSlideUrl) {
                setLoading(false);
                setData(newSlideUrl);
                handleContentDataSave(newSlideUrl);
                setUrl(''); // Clear the input field after successful upload
                successToast('Slides processed and saved successfully!');
              } else if (retryCount < maxRetries) {
                const remainingTime = Math.ceil((maxRetries - retryCount) * 15 / 60);
                logger.info(`URL not updated yet, retrying... (${retryCount + 1}/${maxRetries}) - ${remainingTime} minutes remaining`);
                setTimeout(() => {
                  if (isMounted.current) {
                    fetchSlideUrl(retryCount + 1, maxRetries);
                  }
                }, 15000); // Wait 15 seconds before retrying
              } else {
                setLoading(false);
                errorToast('Slide processing is taking longer than expected. The slides may still be processing in the background. Please check back in a few minutes.');
              }
            } catch (error) {
              logger.error('Error fetching slide URL:', error);
              if (retryCount < maxRetries) {
                setTimeout(() => {
                  if (isMounted.current) {
                    fetchSlideUrl(retryCount + 1, maxRetries);
                  }
                }, 15000);
              } else {
                setLoading(false);
                errorToast('Failed to process slides. Please try again.');
              }
            }
          };
          
          // Start fetching after initial delay
          setTimeout(() => {
            if (isMounted.current) {
              fetchSlideUrl();
            }
          }, 15000); // Increased initial delay to 15 seconds
        } else {
          setLoading(false);
          errorToast('Failed to upload zip file. Please try again.');
        }
      } catch (error) {
        setLoading(false);
        logger.error('Upload error:', error);
        errorToast('Failed to upload zip file. Please check the URL and try again.');
      }
    }
  }, [url, content, data, slideTarget, successToast, errorToast, uploadZIPSlide, getZIPSlideUrl, handleContentDataSave, setData]);

  const handleSetSlideFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e?.target?.files?.[0];
      if (file) {
        try {
          setLoading(true);
          const location = await uploadFile(awsKeys, file, "content", (percent) => {
            setFileUploadingPercent(percent);
          });
          handleContentDataSave(location);
          setData(location);
        } catch (e) {
          alert("Something went wrong");
        } finally {
          setLoading(false);
        }
      }
    },
    [setData, handleContentDataSave, setFileUploadingPercent]
  );

  const handleSetVideoFile = useCallback(
    async (file?: File) => {
      if (file) {
        const videoUrlUploadData = await getUrl({variables: {input: {metadata: file.name}}});
        if (
          videoUrlUploadData.data?.videoUpload?.url &&
          videoUrlUploadData.data?.videoUpload?.uploadId
        ) {
          uploadVideoAndSave(file, {
            url: videoUrlUploadData.data?.videoUpload?.url,
            uploadId: videoUrlUploadData.data?.videoUpload?.uploadId,
          });
        }
      } else {
        clearFile();
      }
    },
    [clearFile, getUrl, uploadVideoAndSave]
  );

  const handleChangeText = useCallback(
    (setter, key?: ErrorKeys) => (value: TextInputValue) => {
      setter(value);
      if (key) {
        setErrors({
          ...errors,
          [key]: "",
        });
      }
    },
    [errors, setErrors]
  );

  const handlePreviewPage = useCallback(() => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage({ type: "PREV_SLIDE" }, "*");
    }
  }, []);

  const handleNextPage = useCallback(() => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage({ type: "NEXT_SLIDE" }, "*");
    }
  }, []);

  const handleSlideType = (value: string) => {
    setExitTicketSlideType(value);
  }

  const contentType = useMemo(() => content?.type, [content?.type]);
  const isSkill = content && content.type === ContentType.SKILL;
  // Use local data state if available, otherwise fall back to content data
  const currentData = data || content?.data;
  const iframeSrc = currentData ? `${currentData}/index.html` : '';

  const jsonFormData = useMemo(() => {
    if (!formData) return null;
    try {
      return JSON.parse(formData) as FeedbackForm;
    } catch (e) {
      logger.info("parse error-", e, formData);
      return null;
    }
  }, [formData]);

  const quizType = useMemo(
    () =>
      Number(jsonFormData?.surveyType) === SURVEY_TYPE.NA_POST_PROGRAM_QUIZ
        ? true
        : false,
    [jsonFormData]
  );

  const genericContent = useCallback(() => {
    switch (contentType) {
      case ContentType.PDF: {
        return (
          <>
            <div className={`${styles.row} ${styles.rowBottom}`}>
              <input
                type="file"
                accept={"application/pdf"}
                onChange={handleSetSlideFile}
                ref={dataInputRef}
              />
              {loading ? (
                <div>{`Uploading ${fileUploadingPercent}%`}</div>
              ) : null}
            </div>
            {data && (
              <div className={`${styles.row} ${styles.preview}`}>
                <ContentPDF data={getMediaAbsolutePath(data)} />
              </div>
            )}
          </>
        );
      }
      case ContentType.SLIDE: {
        return (
          <>
            {data ? (
              <>
                <div className={`${styles.row} ${styles.preview}`}>
                  <ContentSlide data={getMediaAbsolutePath(data)} />
                </div>
                <div className={`${styles.row} ${styles.rowBottomRight}`}>
                  <Button
                    outline
                    disabled={loading}
                    label={
                      loading ? `Uploading ${fileUploadingPercent}%` : "Replace"
                    }
                    size={"extraSmall"}
                    onClick={() => setData("")}
                    className={styles.replaceBtn}
                    leftIcon={<ReloadOutlined style={{ marginRight: 6 }} />}
                  />
                </div>
              </>
            ) : (
              <div className={`${styles.row} ${styles.rowBottom}`}>
                <input
                  type="file"
                  accept={"application/pdf"}
                  onChange={handleSetSlideFile}
                  ref={dataInputRef}
                />
                {loading ? (
                  <div>{`Uploading ${fileUploadingPercent}%`}</div>
                ) : null}
              </div>
            )}
          </>
        );
      }
      case ContentType.ZIPSLIDE: {
        return (
          <div className={styles.flexRow}>
            <div>
              {needsHomeEdSave ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={'Please save the new Home Ed module first — set "Content different from Default" and click Save, then upload your slides.'}
                />
              ) : null}
              <div className={styles.newSlideUpload}>
                <TextInput
                  label={""}
                  id={"upload-input"}
                  placeholder="Paste Zip Slides.com URL"
                  value={url}
                  disabled={needsHomeEdSave || zipUploading || getSlideURLoading || loading}
                  onChange={(value: TextInputValue) => {
                    setUrl(`${value}`);
                  }}
                  className={styles.revealURLEditor}
                />
                <Button
                  label=""
                  className={styles.uploadBtn}
                  onClick={onUpload}
                  disabled={needsHomeEdSave || url === '' || zipUploading || getSlideURLoading || loading}
                  leftIcon={zipUploading || getSlideURLoading ? <Spinner width={20} height={20} /> : <PreviewIcon />}
                />
              </div>
            </div>
            <div className={styles.previewContainer}>
            {loading ? <div className={styles.flex}>
              <label className={styles.label}>Preview</label>
              <Skeleton height={15} width={960}/>
            </div> :
            (isValidUrl(iframeSrc) && 
              <div className={styles.row}>
                <div className={styles.flex}>
                  <label className={styles.label}>Preview</label>
                  <div
                    className={`${styles.row} ${styles.newSlidePreview}`}
                  >
                    <iframe
                      ref={iframeRef}
                      src={iframeSrc}
                      width="960px"
                      height="540px"
                      style={{ border: 'none' }}
                      title="Slide"
                    />
                    <div className={styles.flexJustifyBetween}>
                      <Button disabled={currentPage === 1} label="Previous" size="small" className={styles.slideButton} onClick={handlePreviewPage}/>
                      <span className={styles.pages}>{`${currentPage}/${totalPage}`}</span>
                      <Button disabled={currentPage === totalPage} label="Next" size="small" className={styles.slideButton} onClick={handleNextPage}/>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        );
      }
      case ContentType.REVEALSLIDE: {
        return (
          <>
            <div className={styles.flexRow}>
              <div className={styles.row}>
                <CodeEditor
                  onSubmit={(data) => {
                    setData(data);
                    handleContentDataSave(data);
                  }}
                  onSetUrl={setDescription}
                  onSetData={setData}
                  data={data}
                  url={description}
                />
              </div>
              <div className={styles.previewContainer}>
                {data ? (
                  <div className={styles.row}>
                    <div className={styles.flex}>
                      <label className={styles.label}>Preview</label>
                      <div
                        className={`${styles.row} ${styles.revealSlidePreview}`}
                      >
                        <RevealSlide data={data} key={data} controls />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`${styles.row} ${styles.rowBottom}`}>
                    <div className={styles.row}>
                      <div className={styles.flex}>
                        <label className={styles.label}>Preview</label>
                        <div className={styles.revealSlidePreview}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        );
      }
      case ContentType.SKILL: {
        return (
          <>
            <div className={`${styles.row} ${styles.rowBottom}`}>
              <input
                type="file"
                accept={"audio/*"}
                onChange={handleSetSlideFile}
                ref={dataInputRef}
              />
              {loading ? (
                <div>{`Uploading ${fileUploadingPercent}%`}</div>
              ) : null}
            </div>
            {data && (
              <div className={`${styles.row} ${styles.preview}`}>
                <ContentAudio data={data} />
              </div>
            )}
          </>
        );
      }
      case ContentType.AUDIO: {
        return (
          <>
            <div className={`${styles.row} ${styles.rowBottom}`}>
              <input
                type="file"
                accept={"audio/*"}
                onChange={handleSetSlideFile}
                ref={dataInputRef}
              />
              {loading ? (
                <div>{`Uploading ${fileUploadingPercent}%`}</div>
              ) : null}
            </div>
            {data && (
              <div className={`${styles.row} ${styles.preview}`}>
                <ContentAudio data={data} />
              </div>
            )}
          </>
        );
      }
      case ContentType.VIDEO: {
        return (
          <VideoForm
            data={data || undefined}
            meta={content!.meta}
            onChange={handleSetVideoFile}
            onChangeVideo={(video) => video && handleChangeVideo(video)}
            isUploading={(fileLoading || overallLoading) && !isGroupRemoving}
            uploadingPercent={fileUploadingPercent}
            isDisabled={isDisabled}
            fileUploadFinished={fileUploadFinished}
          />
        );
      }
      case ContentType.FORM: {
        return (
          <>
            {/* Always show the pre-exit ticket slide section, regardless of quizType */}
            <>
              <div className={styles.preExitTicketTitle}>
                Pre-Exit Ticket Slide
              </div>
              {data ? (isFilePath(data) ? // data is PDF file path
                <>
                  <div className={`${styles.row} ${styles.preview}`}>
                    <ContentSlide data={getMediaAbsolutePath(data)} />
                  </div>
                  <div className={`${styles.row} ${styles.rowBottomRight}`}>
                    <Button
                      outline
                      disabled={loading}
                      label={
                        loading
                          ? `Uploading ${fileUploadingPercent}%`
                          : "Replace"
                      }
                      size={"extraSmall"}
                      onClick={() => setData("")}
                      className={styles.replaceBtn}
                      leftIcon={<ReloadOutlined style={{ marginRight: 6 }} />}
                    />
                  </div>
                </> : <>
                <div className={styles.row}>
                  <div className={styles.flex}>
                    <label className={styles.label}>Preview</label>
                    <div
                      className={`${styles.row} ${styles.newSlidePreview}`}
                    >
                      <iframe
                        ref={iframeRef}
                        src={iframeSrc}
                        width="960px"
                        height="540px"
                        style={{ border: 'none' }}
                        title="Slide"
                      />
                      <div className={styles.flexJustifyBetween}>
                        <Button disabled={currentPage === 1} label="Previous" size="small" className={styles.slideButton} onClick={handlePreviewPage}/>
                        <span className={styles.pages}>{`${currentPage}/${totalPage}`}</span>
                        <Button disabled={currentPage === totalPage} label="Next" size="small" className={styles.slideButton} onClick={handleNextPage}/>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`${styles.row} ${styles.rowBottomRight}`}>
                  <Button
                    outline
                    disabled={loading}
                    label="Replace"
                    size={"extraSmall"}
                    onClick={() => setData("")}
                    className={styles.replaceBtn}
                    leftIcon={<ReloadOutlined style={{ marginRight: 6 }} />}
                  />
                </div>
              </>
              ) : (<>
                <div style={{marginBottom: 20}}>
                  <RadioGroup
                    value={exitTicketSlideType}
                    options={exitTicketTypeOptions}
                    onChange={handleSlideType}
                    name={''}
                    hasError={false}
                    subTitle={""}
                  />
                </div>
                {exitTicketSlideType === exitTicketTypeOptions[0].value &&
                <div className={`${styles.row} ${styles.rowBottom}`}>
                  <input
                    type="file"
                    accept={"application/pdf"}
                    onChange={handleSetSlideFile}
                    ref={dataInputRef}
                  />
                  {loading ? (
                    <div>{`Uploading ${fileUploadingPercent}%`}</div>
                  ) : null}
                </div>  
                }
                {exitTicketSlideType === exitTicketTypeOptions[1].value && <div className={styles.flexRow}>
                  <div className={styles.newSlideUpload}>
                    <TextInput
                      label={""}
                      id={"upload-input"}
                      placeholder="Paste Zip Slides.com URL"
                      value={url}
                      disabled={zipUploading || getSlideURLoading || loading}
                      onChange={(value: TextInputValue) => {
                        setUrl(`${value}`);
                      }}
                      className={styles.revealURLEditor}
                    />
                    <Button
                      label=""
                      className={styles.uploadBtn}
                      onClick={onUpload}
                      disabled={zipUploading || getSlideURLoading || loading}
                      leftIcon={zipUploading || getSlideURLoading ? <Spinner width={20} height={20} /> : <PreviewIcon />}
                    />
                  </div>
                  {loading ? <div className={styles.flex}>
                    <label className={styles.label}>Preview</label>
                    <Skeleton height={15} width={960}/>
                  </div> : null}
                </div>
                }
                </>
              )}
            </>
            <div className={styles.divider} />
            <FeedbackFormEditor
              showError={!!errors.feedbackForm}
              formData={formData}
              studentQuiz={quizType}
            />
          </>
        );
      }
      default: {
        return null;
      }
    }
  }, [
    contentType,
    handleSetSlideFile,
    dataInputRef,
    loading,
    fileUploadingPercent,
    data,
    setData,
    content,
    handleSetVideoFile,
    fileLoading,
    overallLoading,
    isGroupRemoving,
    isDisabled,
    fileUploadFinished,
    handleChangeVideo,
    quizType,
    errors.feedbackForm,
    formData,
    handleContentDataSave,
    description,
    setDescription,
    onUpload,
    url,
    zipUploading,
    getSlideURLoading,
    iframeSrc,
    currentPage,
    totalPage,
    handlePreviewPage,
    handleNextPage,
    exitTicketSlideType,
  ]);

  const handleRemoveModuleData = useCallback(() => {
    if (isDefault) {
      if (content?.id) {
        deleteModuleData({ variables: { input: { id: content?.id } } });
      }
    } else {
      if (content?.id && content?.groupId) {
        deleteGroupModuleData({
          variables: { input: { id: content?.id, group: content?.groupId } },
        });
      }
    }
  }, [
    content?.groupId,
    content?.id,
    deleteGroupModuleData,
    deleteModuleData,
    isDefault,
  ]);

  const isHomeEdTab = useMemo(
    () => currentUnitType === UnitTypes.HomeEd,
    [currentUnitType]
  );

  return (
    <>
      {isHomeEdTab && isDefault ? (
        <div className={styles.radioContainer}>
          <div>HE has content for this Module</div>
          <div className={styles.radioGroup}>
            <RadioGroup
              value={homeEdState}
              options={heaOptions}
              onChange={onChangeHomeEdUseDefaultState}
              name={"HE has content for this Module"}
              hasError={false}
              subTitle={""}
            />
          </div>
        </div>
      ) : null}
      {isHomeEdTab &&
      homeEdState === DefaultModuleEnum.Default &&
      isDefault ? null : (
        <>
          {!isDefault ? (
            <div className={`${styles.row} ${styles.rightRow}`}>
              <Button
                color="red"
                size="small"
                disabled={isDisabled}
                isLoading={isGroupRemoving}
                label={"Remove Group"}
                className={styles.removeGroupBtn}
                onClick={handleRemoveGroup}
              />
            </div>
          ) : (
            <div>&nbsp;</div>
          )}
          {isSkill && (
            <div className={styles.row}>
              <TextInput
                textArea={true}
                allowSpecialCharacters={false}
                label={"Description"}
                value={description === "Content description" ? "" : description}
                id={"description"}
                placeholder="Description"
                onChange={handleChangeText(setDescription)}
              />
            </div>
          )}
          {/* <div className={styles.row}>
            <TextInput hasError={!!errors.position} subTitle={errors.position} type={'number'} label={'Position'}
                      id={'position'} value={position} onChange={handleChangeText(setPosition, 'position')}/>
          </div> */}
          {/* {!isParent && !isSkill &&
          <div className={styles.row}>
            <SelectMulti label={'Groups'} options={groupOptions} onChange={handleChangeGroups} value={groups}/>
          </div>
          }
          {isFormType && !isParent && !isSkill &&
          <div className={styles.row}>
            <Checkbox id={'pauseHere'} checked={pauseHere} onChange={handleChangePauseHere} label={'Pause here'}/>
          </div>
          } */}
          {genericContent()}
          {isHomeEdTab ? null : (
            <>
              {data && contentType !== ContentType.FORM ? (
                <div className={styles.buttonContainer}>
                  <div>
                    <Button
                      color="red"
                      onClick={handleRemoveModuleData}
                      outline={true}
                      size={"small"}
                      disabled={isDisabled || isGroupRemoving}
                      leftIcon={
                        isGroupRemoving ? (
                          <Spinner width={20} height={20} />
                        ) : undefined
                      }
                      label={"DELETE CONTENT"}
                    />
                  </div>
                </div>
              ) : null}
              {isDefault && isClassPresentation ? (
                <div className={`${styles.lastRow} ${styles.marginTop}`}>
                  <Checkbox
                    id={"required"}
                    checked={isRequired}
                    onChange={onChangeRequiredLesson}
                    label={"Required Lesson Material"}
                  />
                  <Checkbox
                    id={"isLastLesson"}
                    checked={isLastLesson}
                    onChange={onChangeLastLessonMark}
                    label={"Mark lesson completed by this module"}
                  />
                  {contentType !== ContentType.FORM && (
                    <Checkbox
                      id={"isAdditionalAfter"}
                      checked={isAdditionalAfter}
                      onChange={onChangeAdditionalAfter}
                      label={
                        "This is the final exercise for this Lesson. The next Slides will be Additional Content if the Teacher has time."
                      }
                    />
                  )}
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </>
  );
}

export default GenericContent;
